import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  exportComplianceReportsCsv,
  exportComplianceReportsExcel,
  getComplianceReports,
  getComplianceReportSummary,
} from '@/api/services/reports.service'
import { getErrorMessage } from '@/lib/getErrorMessage'
import { usePermittedQuery } from '@/hooks/usePermittedQuery'
import { usePermissions } from '@/hooks/usePermissions'
import type {
  ComplianceReportExportFormat,
  ComplianceReportQuery,
  ComplianceReportSummaryFilters,
} from '@/features/reports/types/compliance-report.types'

export const complianceReportsQueryKeys = {
  all: ['reports', 'compliance'] as const,
  list: (query: ComplianceReportQuery | undefined) =>
    ['reports', 'compliance', 'list', query] as const,
  summary: (filters: ComplianceReportSummaryFilters | undefined) =>
    ['reports', 'compliance', 'summary', filters] as const,
}

export function useComplianceReports(query: ComplianceReportQuery | undefined) {
  return usePermittedQuery({
    module: 'reports',
    action: 'read',
    queryKey: complianceReportsQueryKeys.list(query),
    queryFn: () => getComplianceReports(query),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

export function useComplianceReportSummary(
  filters: ComplianceReportSummaryFilters | undefined
) {
  return usePermittedQuery({
    module: 'reports',
    action: 'read',
    queryKey: complianceReportsQueryKeys.summary(filters),
    queryFn: () => getComplianceReportSummary(filters),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

function downloadExport(blob: Blob, format: ComplianceReportExportFormat): void {
  const ext = format === 'excel' ? 'xlsx' : 'csv'
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `compliance-reports-${new Date().toISOString().slice(0, 10)}.${ext}`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export function useExportComplianceReports(format: ComplianceReportExportFormat) {
  const { can } = usePermissions()

  return useMutation({
    mutationFn: async (filters: ComplianceReportSummaryFilters | undefined) => {
      if (!can('reports', 'export')) {
        throw new Error('You do not have permission to export reports.')
      }
      return format === 'excel'
        ? exportComplianceReportsExcel(filters)
        : exportComplianceReportsCsv(filters)
    },
    onSuccess: (blob) => {
      downloadExport(blob, format)
      toast.success(`${format === 'excel' ? 'Excel' : 'CSV'} export downloaded`)
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}
